import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'

const shell =
  'fixed inset-0 z-[60] flex items-center justify-center p-[var(--stryzh-space-4)] bg-[color:var(--stryzh-overlay-scrim-modal)]'

const panelClass =
  'stryzh-panel w-full max-w-[var(--stryzh-layout-modal-max-width)] overflow-hidden'

export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel,
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
}: {
  open: boolean
  title?: string
  children: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  confirmVariant?: 'primary' | 'danger'
  onConfirm: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  if (!open) return null

  return (
    <div
      className={shell}
      role="presentation"
      onClick={onCancel}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        className={panelClass}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-[var(--stryzh-space-6)] pb-[var(--stryzh-space-3)] pt-[var(--stryzh-space-5)]">
          <h2
            id="confirm-dialog-title"
            className="font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]"
          >
            {title ?? t('confirm.title')}
          </h2>
        </div>
        <div className="px-[var(--stryzh-space-6)] py-[var(--stryzh-space-3)]">
          <div className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] leading-relaxed text-[color:var(--stryzh-color-text-primary)]">
            {children}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-[var(--stryzh-space-6)] pb-[var(--stryzh-space-5)] pt-[var(--stryzh-space-3)]">
          <Button type="button" variant="outline" onClick={onCancel}>
            {cancelLabel ?? t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            onClick={onConfirm}
          >
            {confirmLabel ?? t('confirm.ok')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export function AlertDialog({
  open,
  title,
  message,
  actionLabel,
  onClose,
}: {
  open: boolean
  title?: string
  message: string
  actionLabel?: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  if (!open) return null

  return (
    <div
      className={shell}
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="alert-dialog-title"
        className={panelClass}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-[var(--stryzh-space-6)] pb-[var(--stryzh-space-3)] pt-[var(--stryzh-space-5)]">
          <h2
            id="alert-dialog-title"
            className="font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]"
          >
            {title ?? t('confirm.alertTitle')}
          </h2>
        </div>
        <div className="px-[var(--stryzh-space-6)] py-[var(--stryzh-space-3)]">
          <p className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] leading-relaxed text-[color:var(--stryzh-color-text-primary)]">
            {message}
          </p>
        </div>
        <div className="flex justify-end px-[var(--stryzh-space-6)] pb-[var(--stryzh-space-5)] pt-[var(--stryzh-space-3)]">
          <Button type="button" variant="primary" onClick={onClose}>
            {actionLabel ?? t('confirm.ok')}
          </Button>
        </div>
      </div>
    </div>
  )
}
